/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { cloneDeep, sortBy } from 'lodash'

import type {
  DeploymentTemplateInfoV2,
  DeploymentUpdateRequest,
  ElasticsearchClusterTopologyElement,
  InstanceConfiguration,
} from '@modules/cloud-api/v1/types'
import type { AnyTopologyElement, ZoneSummaryInstance } from '@modules/ui-types'

import { isDeprecatedConfig } from '@/lib/instanceConfigurations/instanceConfiguration'
import { getNodeRoles, getTier, type DataTier } from '@/lib/stackDeployments/selectors/nodeRoles'

export type TierInstanceConfigurations = {
  hot?: string
  warm?: string
  cold?: string
  frozen?: string
}

/**
 * Creates a ZoneSummaryInstance from an InstanceConfiguration and topology element
 * for use with HardwareSummary component
 */
export function createZoneSummaryInstance(
  instanceConfiguration: InstanceConfiguration,
  templateTopologyElement: ElasticsearchClusterTopologyElement,
): ZoneSummaryInstance {
  const currentSize = templateTopologyElement.size?.value
  return {
    id: instanceConfiguration.id || '',
    name: instanceConfiguration.name || instanceConfiguration.id || '',
    type: 'elasticsearch',
    topologyId: templateTopologyElement.id,
    sliderInstanceType: 'elasticsearch',
    sliderNodeType: instanceConfiguration.node_types as ZoneSummaryInstance['sliderNodeType'],
    // Required by ZoneSummaryInstance type but not used in this context
    size: undefined,
    totalSize: currentSize,
    storageMultiplier: instanceConfiguration.storage_multiplier,
    cpuMultiplier: instanceConfiguration.cpu_multiplier,
    icConfigVersion: instanceConfiguration.config_version,
  }
}

export function isLegacyInstanceConfiguration(ic: InstanceConfiguration): boolean {
  if (!ic.metadata) {
    return false
  }

  return ic.metadata.legacy === 'true' || ic.metadata.legacy === true
}

export function isDeletedInstanceConfiguration(ic: InstanceConfiguration): boolean {
  return ic.deleted_on !== undefined
}

export function hasDataNodeTypes(ic: InstanceConfiguration): boolean {
  return ic.node_types?.includes('data') ?? false
}

/**
 * Checks if an IC's node_types are compatible with a hot tier's node_roles
 * For hot tier: IC node_types must include 'master' and/or 'ingest' if the tier has those roles
 */
export function isICCompatibleWithHotTier(
  ic: InstanceConfiguration,
  hotTopologyElement: ElasticsearchClusterTopologyElement,
): boolean {
  if (!ic.node_types || !ic.node_types.includes('data')) {
    return false
  }

  const tierRoles = getNodeRoles({ topologyElement: hotTopologyElement })
  const icNodeTypes = ic.node_types

  // Check if tier has master role - IC must also have master
  if (tierRoles.includes('master') && !icNodeTypes.includes('master')) {
    return false
  }

  // Check if tier has ingest role - IC must also have ingest
  if (tierRoles.includes('ingest') && !icNodeTypes.includes('ingest')) {
    return false
  }

  return true
}

/**
 * Filters instance configurations for a specific tier.
 * Only returns ICs that are:
 * - Not deleted
 * - Elasticsearch type
 * - Not deprecated
 * - Not legacy (unless allowed by org)
 * - Have 'data' in node_types
 * - Compatible with hot tier requirements (if hot tier)
 *
 * ICs in allowedICIds bypass all filtering (used for current template ICs).
 */
export function filterInstanceConfigurationsForTier({
  instanceConfigurations,
  tier,
  topologyElement,
  allowLegacy = false,
  allowedICIds = [],
}: {
  instanceConfigurations: InstanceConfiguration[]
  tier: DataTier
  topologyElement?: AnyTopologyElement
  allowLegacy?: boolean
  allowedICIds?: string[]
}): InstanceConfiguration[] {
  return instanceConfigurations.filter((ic) => {
    if (ic.id && allowedICIds.includes(ic.id)) {
      return true
    }

    if (isDeletedInstanceConfiguration(ic)) {
      return false
    }

    if (ic.instance_type !== 'elasticsearch') {
      return false
    }

    if (isDeprecatedConfig(ic)) {
      return false
    }

    if (!allowLegacy && isLegacyInstanceConfiguration(ic)) {
      return false
    }

    if (!hasDataNodeTypes(ic)) {
      return false
    }

    // Hot tier has special compatibility requirements (master/ingest roles)
    if (tier === 'hot' && topologyElement) {
      return isICCompatibleWithHotTier(ic, topologyElement as ElasticsearchClusterTopologyElement)
    }

    return true
  })
}

export function getTemplateInstanceConfigurationsByTier(
  template: DeploymentTemplateInfoV2,
): TierInstanceConfigurations {
  const result: TierInstanceConfigurations = {}

  const esResources = template.deployment_template.resources?.elasticsearch

  if (!esResources || esResources.length === 0) {
    return result
  }

  const topology = esResources[0]?.plan?.cluster_topology || []

  for (const topologyElement of topology) {
    const tier = getTier(topologyElement)

    if (tier && topologyElement.instance_configuration_id) {
      result[tier] = topologyElement.instance_configuration_id
    }
  }

  return result
}

/**
 * Extracts the current instance configuration IDs by tier from the deployment's ES topology.
 * This returns what the deployment is ACTUALLY using (may differ from template defaults).
 */
export function getDeploymentInstanceConfigurationsByTier(
  esTopology: ElasticsearchClusterTopologyElement[],
): TierInstanceConfigurations {
  const result: TierInstanceConfigurations = {}

  for (const topologyElement of esTopology) {
    const tier = getTier(topologyElement)

    if (tier && topologyElement.instance_configuration_id) {
      result[tier] = topologyElement.instance_configuration_id
    }
  }

  return result
}

/**
 * Compares two TierInstanceConfigurations to see if they match across all data tiers.
 * Returns true if source IC matches target IC, or if source doesn't have the tier.
 */
function doTierConfigsMatch(
  sourceTierConfigs: TierInstanceConfigurations,
  targetTierConfigs: TierInstanceConfigurations,
): boolean {
  const tiers: DataTier[] = ['hot', 'warm', 'cold', 'frozen']

  return tiers.every((tier) => {
    const sourceIC = sourceTierConfigs[tier]
    const targetIC = targetTierConfigs[tier]

    // If source doesn't have this tier, it's a match (source just doesn't use it)
    if (!sourceIC) {
      return true
    }

    return sourceIC === targetIC
  })
}

/**
 * Finds a deployment template that matches the given tier IC configurations
 */
export function findMatchingTemplate(
  tierConfigs: TierInstanceConfigurations,
  deploymentTemplates: DeploymentTemplateInfoV2[],
): DeploymentTemplateInfoV2 | null {
  for (const template of deploymentTemplates) {
    const templateConfigs = getTemplateInstanceConfigurationsByTier(template)

    if (doTierConfigsMatch(tierConfigs, templateConfigs)) {
      return template
    }
  }

  return null
}

export function doDeploymentICsMatchTemplate(
  deploymentTopology: ElasticsearchClusterTopologyElement[],
  template: DeploymentTemplateInfoV2,
): boolean {
  const deploymentConfigs = getDeploymentInstanceConfigurationsByTier(deploymentTopology)
  const templateConfigs = getTemplateInstanceConfigurationsByTier(template)

  // usually the deployment topology for all compoenents will match whatever we have in the template,
  // hot, warm, cold, frozen are the only allowed changes on the ES level, we do not allow changes on
  // IC for other components like Kibana, APM, etc. The logic here is to only check the ES level for changes.
  return doTierConfigsMatch(deploymentConfigs, templateConfigs)
}

/**
 * Applies custom tier IC configurations to a DeploymentUpdateRequest payload.
 * This modifies the instance_configuration_id and instance_configuration_version for
 * each ES topology element based on the tier.
 * Size and zones are preserved from the template payload.
 */
export function applyTierConfigurationsToPayload({
  migratedTemplatePayload,
  tierConfigurations,
  esTopology,
  instanceConfigurations,
}: {
  /** The template's payload (from migrate_template API) */
  migratedTemplatePayload: DeploymentUpdateRequest
  /** User's IC selections per tier */
  tierConfigurations: TierInstanceConfigurations
  /** Current deployment's ES topology */
  esTopology: ElasticsearchClusterTopologyElement[]
  /** Available ICs for the region (used to look up version) */
  instanceConfigurations?: InstanceConfiguration[]
}): DeploymentUpdateRequest {
  const modifiedPayload = cloneDeep(migratedTemplatePayload)

  const esResources = modifiedPayload.resources?.elasticsearch

  if (!esResources || esResources.length === 0) {
    return modifiedPayload
  }

  const topology = esResources[0]?.plan?.cluster_topology

  if (!topology) {
    return modifiedPayload
  }

  for (const topologyElement of topology) {
    const tier = getTier(topologyElement)

    if (!tier) {
      continue
    }

    // Look up IC version from instanceConfigurations; size is preserved from template payload
    const selectedICId = tierConfigurations[tier]

    if (selectedICId && instanceConfigurations) {
      const selectedIC = instanceConfigurations.find((ic) => ic.id === selectedICId)

      if (selectedIC) {
        topologyElement.instance_configuration_id = selectedICId
        topologyElement.instance_configuration_version = selectedIC.config_version
      }
    }
  }

  // Reorder topology elements to match the current deployment's order.
  // This is needed because getBaseSettingsPaths() in lib/stackDeployments/planDiffs/settings.ts
  // compares elements by array index, so mismatched ordering causes incorrect "settings removed" warnings.
  // TODO: fix the actual issue with the settings diffs, this is a workaround.
  if (esResources[0]?.plan?.cluster_topology) {
    esResources[0].plan.cluster_topology = sortTopologyToMatchDeployment(topology, esTopology)
  }

  return modifiedPayload
}

/** Sorts topology to match current deployment's order (by element id). */
function sortTopologyToMatchDeployment(
  newTopology: ElasticsearchClusterTopologyElement[],
  currentTopology: ElasticsearchClusterTopologyElement[],
): ElasticsearchClusterTopologyElement[] {
  return sortBy(newTopology, (el) => {
    const index = currentTopology.findIndex((curr) => curr.id === el.id)
    return index === -1 ? Infinity : index
  })
}
